import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Trash2 } from 'lucide-react';
import { apiClient } from '../../utils/apiHelper';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IssueStock = () => {
  const [items, setItems] = useState([]);
  const [issues, setIssues] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    issue_number: `ISS-${Date.now()}`,
    issue_date: new Date().toISOString().split('T')[0],
    issued_to: '',
    issued_to_type: 'Department',
    purpose: '',
    issued_by: 'Admin',
    items: []
  });
  const [selectedItems, setSelectedItems] = useState([{ item_id: '', item_name: '', quantity: 1, unit_price: 0, total: 0 }]);

  useEffect(() => {
    fetchItems();
    fetchIssues();
    fetchDepartments();
    fetchStaff();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await apiClient.get('/api/inventory/items');
      const data = Array.isArray(response.data) ? response.data : response.data;
      setItems(data);
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  const fetchIssues = async () => {
    try {
      const response = await apiClient.get('/api/inventory/stock-issues');
      const data = Array.isArray(response.data) ? response.data : response.data;
      setIssues(data);
    } catch (error) {
      console.error('Error fetching issues:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await apiClient.get('/api/master-settings/departments');
      const data = Array.isArray(response.data) ? response.data : [];
      setDepartments(data);
    } catch (error) {
      console.error('Error fetching departments:', error);
      setDepartments([]);
    }
  };

  const fetchStaff = async () => {
    try {
      const response = await apiClient.get('/api/staff/');
      const data = Array.isArray(response.data) ? response.data : [];
      setStaffList(data);
    } catch (error) {
      console.error('Error fetching staff:', error);
      setStaffList([]);
    }
  };

  const handleItemChange = (index, field, value) => {
    const updated = [...selectedItems];
    updated[index][field] = value;

    if (field === 'item_id') {
      const item = items.find(i => i.id === value);
      if (item) {
        updated[index].item_name = item.item_name;
        updated[index].unit_price = item.unit_price;
        updated[index].total = updated[index].quantity * item.unit_price;
      }
    } else if (field === 'quantity') {
      updated[index].total = value * updated[index].unit_price;
    }

    setSelectedItems(updated);
  };

  const addItemRow = () => {
    setSelectedItems([...selectedItems, { item_id: '', item_name: '', quantity: 1, unit_price: 0, total: 0 }]);
  };

  const removeItemRow = (index) => {
    setSelectedItems(selectedItems.filter((_, i) => i !== index));
  };

  const calculateTotal = () => {
    return selectedItems.reduce((sum, item) => sum + item.total, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const validItems = selectedItems.filter(item => item.item_id && item.quantity > 0);
    if (validItems.length === 0) {
      alert('Please add at least one item');
      setLoading(false);
      return;
    }

    try {
      const response = await apiClient.post('/api/inventory/stock-issues', {
        ...formData,
        items: validItems,
        total_value: calculateTotal()
      });

      if (response.status === 200 || response.data) {
        alert('Stock issued successfully!');
        fetchIssues();
        // Reset form
        setFormData({
          issue_number: `ISS-${Date.now()}`,
          issue_date: new Date().toISOString().split('T')[0],
          issued_to: '',
          issued_to_type: 'Department',
          purpose: '',
          issued_by: 'Admin',
          items: []
        });
        setSelectedItems([{ item_id: '', item_name: '', quantity: 1, unit_price: 0, total: 0 }]);
      } else {
        alert('Failed to issue stock');
      }
    } catch (error) {
      console.error('Error issuing stock:', error);
      alert(error.response?.data?.detail || 'Failed to issue stock');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteIssue = async (issue) => {
    if (!window.confirm(`Delete stock issue ${issue.issue_number}? Items will be returned to inventory.`)) return;
    try {
      await apiClient.delete(`/api/inventory/stock-issues/${issue.id}`);
      alert('Stock issue deleted. Inventory restored.');
      fetchIssues();
      fetchItems();
    } catch (e) {
      alert(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Issue Stock</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Issue Number</Label>
                <Input value={formData.issue_number} disabled />
              </div>
              <div>
                <Label>Issue Date *</Label>
                <Input
                  type="date"
                  required
                  value={formData.issue_date}
                  onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Issued By</Label>
                <Input
                  value={formData.issued_by}
                  onChange={(e) => setFormData({ ...formData, issued_by: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Issued To Type *</Label>
                <Select value={formData.issued_to_type} onValueChange={(value) => setFormData({ ...formData, issued_to_type: value, issued_to: '' })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Department">Department</SelectItem>
                    <SelectItem value="Staff">Staff</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Issued To *</Label>
                {formData.issued_to_type === 'Department' ? (
                  <Select value={formData.issued_to} onValueChange={(value) => setFormData({ ...formData, issued_to: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="-- Select Department --" />
                    </SelectTrigger>
                    <SelectContent>
                      {departments.map((dept) => (
                        <SelectItem key={dept.id} value={dept.name || dept.department_name}>
                          {dept.name || dept.department_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : formData.issued_to_type === 'Staff' ? (
                  <Select value={formData.issued_to} onValueChange={(value) => setFormData({ ...formData, issued_to: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="-- Select Staff --" />
                    </SelectTrigger>
                    <SelectContent>
                      {staffList.map((staff) => (
                        <SelectItem key={staff.id} value={staff.full_name || staff.name}>
                          {staff.full_name || staff.name} ({staff.designation || 'Staff'})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    required
                    value={formData.issued_to}
                    onChange={(e) => setFormData({ ...formData, issued_to: e.target.value })}
                    placeholder="Enter name"
                  />
                )}
              </div>
            </div>

            <div>
              <Label>Purpose</Label>
              <Input
                value={formData.purpose}
                onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                placeholder="Purpose of issuance"
              />
            </div>

            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex justify-between items-center">
                <h3 className="font-medium">Items</h3>
                <Button type="button" size="sm" onClick={addItemRow}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Item
                </Button>
              </div>

              {selectedItems.map((item, index) => (
                <div key={index} className="grid grid-cols-6 gap-2 items-end">
                  <div className="col-span-2">
                    <Label>Item</Label>
                    <Select value={item.item_id} onValueChange={(value) => handleItemChange(index, 'item_id', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select item" />
                      </SelectTrigger>
                      <SelectContent>
                        {items.map((i) => (
                          <SelectItem key={i.id} value={i.id}>
                            {i.item_name} (Stock: {i.quantity})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Quantity</Label>
                    <Input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value))}
                    />
                  </div>
                  <div>
                    <Label>Unit Price</Label>
                    <Input value={item.unit_price} disabled />
                  </div>
                  <div>
                    <Label>Total</Label>
                    <Input value={item.total} disabled />
                  </div>
                  <div>
                    <Button type="button" size="sm" variant="destructive" onClick={() => removeItemRow(index)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}

              <div className="text-right font-bold text-lg pt-3 border-t">
                Total: ₹{calculateTotal()}
              </div>
            </div>

            <Button type="submit" disabled={loading} className="w-full">
              {loading ? 'Processing...' : 'Issue Stock'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent Stock Issues</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3">Issue #</th>
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">Issued To</th>
                  <th className="text-left p-3">Type</th>
                  <th className="text-left p-3">Total Value</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {issues.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center p-4 text-gray-500">
                      No stock issues found.
                    </td>
                  </tr>
                ) : (
                  issues.map((issue) => (
                    <tr key={issue.id} className="border-b hover:bg-gray-50">
                      <td className="p-3 font-medium">{issue.issue_number}</td>
                      <td className="p-3">{formatDate(issue.issue_date)}</td>
                      <td className="p-3">{issue.issued_to}</td>
                      <td className="p-3">{issue.issued_to_type}</td>
                      <td className="p-3">₹{issue.total_value}</td>
                      <td className="p-3">
                        <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                          {issue.status}
                        </span>
                      </td>
                      <td className="p-3">
                        <button
                          onClick={() => handleDeleteIssue(issue)}
                          className="text-red-600 hover:text-red-800 p-1"
                          title="Delete & restore inventory"
                          data-testid={`delete-issue-${issue.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default IssueStock;